import { BusMessage, calcLocalizationStringByteLength, calcObjectByteLength, calcStringByteLength, MessageFactory, UID_SAFE_BYTES_PER_ELEMENT } from "flavr-common";
/**
 * Carries a generic input action payload.
 */
export class InputActionMessage extends BusMessage {
    get byteLength() {
        return UID_SAFE_BYTES_PER_ELEMENT + Uint32Array.BYTES_PER_ELEMENT; // actionIdx
    }
    constructor(actionIdx, uid) {
        super();
        this.actionIdx = actionIdx;
        this.uid = uid;
    }
    serialization(stream) {
        this.actionIdx = stream.uint32(this.actionIdx);
        this.uid = stream.uidSafe(this.uid);
    }
    static is(msg) {
        return msg.type === InputActionMessage.typestatic;
    }
}
InputActionMessage.typestatic = "InputActionMessage";
InputActionMessage.subjectstatic = "general";
MessageFactory.register(InputActionMessage);
/**
 * Registers an input action with the input system.
 */
export class RegisterInputActionMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.name) + calcLocalizationStringByteLength(this.description);
    }
    constructor(name, description) {
        super();
        this.name = name;
        this.description = description;
    }
    serialization(stream) {
        this.name = stream.utf16String(this.name);
        this.description = stream.localizationStringSafe(this.description);
    }
    static is(msg) {
        return msg.type === RegisterInputActionMessage.typestatic;
    }
}
RegisterInputActionMessage.typestatic = "RegisterInputActionMessage";
RegisterInputActionMessage.subjectstatic = "general";
MessageFactory.register(RegisterInputActionMessage);
/**
 * Returns the result of registering an input action.
 */
export class ResponseRegisterInputActionMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    constructor(actionIdx) {
        super();
        this.actionIdx = actionIdx;
    }
    serialization(stream) {
        this.actionIdx = stream.uint32(this.actionIdx);
    }
    static is(msg) {
        return msg.type === ResponseRegisterInputActionMessage.typestatic;
    }
}
ResponseRegisterInputActionMessage.typestatic = "ResponseRegisterInputActionMessage";
ResponseRegisterInputActionMessage.subjectstatic = "general";
MessageFactory.register(ResponseRegisterInputActionMessage);
/**
 * Unregisters an input action from the input system.
 */
export class UnregisterInputActionMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.name);
    }
    constructor(name) {
        super();
        this.name = name;
    }
    serialization(stream) {
        this.name = stream.utf16String(this.name);
    }
    static is(msg) {
        return msg.type === UnregisterInputActionMessage.typestatic;
    }
}
UnregisterInputActionMessage.typestatic = "UnregisterInputActionMessage";
UnregisterInputActionMessage.subjectstatic = "general";
MessageFactory.register(UnregisterInputActionMessage);
/**
 * Announces that an input action was triggered.
 */
export class InputActionTriggeredMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    constructor(actionIdx) {
        super();
        this.actionIdx = actionIdx;
    }
    serialization(stream) {
        this.actionIdx = stream.uint32(this.actionIdx);
    }
    static is(msg) {
        return msg.type === InputActionTriggeredMessage.typestatic;
    }
}
InputActionTriggeredMessage.typestatic = "InputActionTriggeredMessage";
InputActionTriggeredMessage.subjectstatic = "general";
MessageFactory.register(InputActionTriggeredMessage);
/**
 * Publishes key bindings for a registered input action.
 */
export class InputActionKeyBindingsMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT + calcObjectByteLength(this.keys);
    }
    constructor(actionIdx, keys) {
        super();
        this.actionIdx = actionIdx;
        this.keys = keys;
    }
    serialization(stream) {
        this.actionIdx = stream.uint32(this.actionIdx);
        this.keys = stream.object(this.keys);
    }
    static is(msg) {
        return msg.type === InputActionKeyBindingsMessage.typestatic;
    }
}
InputActionKeyBindingsMessage.typestatic = "InputActionKeyBindingsMessage";
InputActionKeyBindingsMessage.subjectstatic = "general";
MessageFactory.register(InputActionKeyBindingsMessage);
/**
 * Reports the current value of an input axis.
 */
export class InputAxisValueMessage extends BusMessage {
    get byteLength() {
        return (Float32Array.BYTES_PER_ELEMENT + // value
            UID_SAFE_BYTES_PER_ELEMENT +
            Uint32Array.BYTES_PER_ELEMENT); // actionIdx
    }
    constructor(value, axisIndex, uid) {
        super();
        this.value = value;
        this.axisIndex = axisIndex;
        this.uid = uid;
    }
    serialization(stream) {
        this.value = stream.float32(this.value);
        this.axisIndex = stream.uint32(this.axisIndex);
        this.uid = stream.uidSafe(this.uid);
    }
    static is(msg) {
        return msg.type === InputAxisValueMessage.typestatic;
    }
}
InputAxisValueMessage.typestatic = "InputAxisValueMessage";
InputAxisValueMessage.subjectstatic = "general";
MessageFactory.register(InputAxisValueMessage);
export var InputFlaVRDefaultActionType;
(function (InputFlaVRDefaultActionType) {
    InputFlaVRDefaultActionType[InputFlaVRDefaultActionType["Primary"] = 0] = "Primary";
    InputFlaVRDefaultActionType[InputFlaVRDefaultActionType["Secondary"] = 1] = "Secondary";
    InputFlaVRDefaultActionType[InputFlaVRDefaultActionType["Tertiary"] = 2] = "Tertiary";
    InputFlaVRDefaultActionType[InputFlaVRDefaultActionType["Quaternary"] = 3] = "Quaternary";
})(InputFlaVRDefaultActionType || (InputFlaVRDefaultActionType = {}));
/**
 * Sends one of the predefined default FlaVR input actions.
 */
export class InputFlaVRDefaultActionMessage extends BusMessage {
    get byteLength() {
        return (UID_SAFE_BYTES_PER_ELEMENT + // inputUid
            Uint8Array.BYTES_PER_ELEMENT); // InputFlaVRDefaultActionType
    }
    constructor(action, inputUid) {
        super();
        this.action = action;
        this.inputUid = inputUid;
    }
    serialization(stream) {
        this.action = stream.uint8(this.action);
        this.inputUid = stream.uidSafe(this.inputUid);
    }
    static is(msg) {
        return msg.type === InputFlaVRDefaultActionMessage.typestatic;
    }
}
InputFlaVRDefaultActionMessage.typestatic = "InputFlaVRDefaultActionMessage";
InputFlaVRDefaultActionMessage.subjectstatic = "general";
MessageFactory.register(InputFlaVRDefaultActionMessage);
